import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../../lib/axios";
import { usePermissions } from "../../../../GlobalFunction/userPermissions";
import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Button,
  Tooltip,
  Breadcrumbs,
  Link,
  Paper,
  Typography,
  Chip,
  CircularProgress
} from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import HomeIcon from '@mui/icons-material/Home';

export default function WardenAllocationTable() {
  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [gridApi, setGridApi] = useState(null);
  const navigate = useNavigate();

  const permissions = usePermissions("Warden/Vendor Allocation");
  const { AddRec = "0" } = permissions || {};
  const handlePermittedClick = () => {
    if (AddRec === "1") {
      handleClick();
    }
  };

  const gridStyles = `
  .ag-theme-alpine {
    --ag-border-color: #c8c8c8;
    --ag-header-background-color: #f8f9fa;
    --ag-odd-row-background-color: #fcfcfc;
    --ag-border-radius: 6px;
  }
  
  .ag-theme-alpine .ag-cell {
    border-right: 1px solid #e0e0e0;
    border-bottom: 1px solid #e0e0e0;
    display: flex;
    align-items: center;
  }
  
  .ag-theme-alpine .ag-header-cell {
    border-right: 1px solid #c8c8c8;
    border-bottom: 1px solid #c8c8c8;
    font-weight: 600;
  }
  
  .ag-theme-alpine .ag-floating-filter-input {
    border-radius: 4px;
  }
  
  .ag-theme-alpine .ag-floating-filter-input:focus {
    outline: none;
    border-color: #3f51b5;
    box-shadow: 0 0 0 2px rgba(63, 81, 181, 0.2);
  }
  
  .ag-theme-alpine .ag-header-cell::after {
    display: none;
  }
  
  .ag-theme-alpine .ag-ltr .ag-cell-first-right-pinned {
    border-left: 1px solid #c8c8c8;
  }
  
  .ag-floating-filter-button{
    margin-left: 0px !important 
  }
  
  .ag-header-cell{
    padding-left: 2px;
    padding-right: 0px;
  }
  
  .truncated-cell {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }
  
  .status-chip {
    height: 20px;
    font-size: 0.75rem;
    font-weight: 600;
  }
  
   .ag-input-wrapper:before {
    background-color: white;
    display:none
  }
    .ag-theme-alpine .ag-floating-filter input[type="date"]{
      padding-left: 0 !important;
    }
      :where(.ag-ltr) .ag-header-cell-resize {
    right: -6px !important;
}


@media (max-width: 600px) {
  /* Parent container: Pagination panel */
  .ag-theme-alpine .ag-paging-panel {
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    justify-content: center !important;
    padding: 10px 12px !important;
    box-sizing: border-box;
    text-align: center;
    width: 100%;
    min-height: 120px; /* 👈 Increased height for breathing room */
    gap: 10px; /* Gap between rows */
  }

  /* Row 1: Total Records + Page Size (side-by-side) */
  .ag-theme-alpine .ag-paging-row-summary-panel,
  .ag-theme-alpine .ag-paging-page-size {
    display: flex !important;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    flex: 1;
    gap: 16px;
    white-space: nowrap;
    margin: 0;
  }

  /* Force them on the same row together */
  .ag-theme-alpine .ag-paging-panel > .ag-paging-row-summary-panel,
  .ag-theme-alpine .ag-paging-panel > .ag-paging-page-size {
    flex: none;
  }

  /* Wrap both inside a row container */
  .ag-theme-alpine .ag-paging-row-summary-panel::after {
    content: '';
    flex-grow: 1;
  }

  /* Row 2: Pagination buttons */
  .ag-theme-alpine .ag-paging-button-panel {
    display: flex !important;
    justify-content: center !important;
    flex-wrap: wrap;
    width: 100%;
    margin-top: 2px;
  }

  /* Optional: Limit width of dropdown for mobile */
  .ag-theme-alpine .ag-paging-page-size select,
  .ag-theme-alpine .ag-paging-page-size input {
    max-width: 70px !important;
    font-size: 14px !important;
    padding: 4px;
  }
}

`;

  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = gridStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.post(
        "TktCategory/GetMstPropWiseServProDashBoard",
        {
          PropId: 1,
          WardenId: 1,
          ServiceProvId: 1,
          TktCategoryId: 1,
          Flag: "Dash",
        }
      );
      const {
        data: { Status, Message, Data },
      } = response;
      if (Status === 0 && Array.isArray(Data) && Message) {
        const formattedData = Data.map((complaint) => ({
          ...complaint,
          TktCategoryName: complaint.TktCategoryName || "",
          TktSubCategoryName: complaint.TktSubCategoryName || "",
          PropId: complaint.PropId,
          TktCategoryId: complaint.TktCategoryId,
          Abrv: complaint.Abrv || "",
          Remark: complaint.Remark || "",
          Status:
            complaint.Status === null
              ? ""
              : complaint.Status == 1
                ? "Active"
                : "Inactive",

        }));
        setRows(formattedData);

      } else {
        console.error("Error fetching complaint master data:", Message);
      }
    } catch (error) {
      console.error("Error fetching complaint master data:", error);
    }
    finally {
      setIsLoading(false);
    }
  };

  const handleClick = () => {
    navigate("/warden-allocation", { state: { mode: "add" } });
  };

  const handleHomeClick = () => {
    navigate("/dashboard");
  };
  const handleRowDoubleClick = useCallback((params) => {
    const rowId = params?.data?.Id;
    const propId = params?.data.PropId;
    if (rowId && propId) {
      navigate('/warden-allocation', {
        state: {
          TktCategoryId: rowId,
          PropId: propId,
          mode: 'view'
        }
      });
    }
  }, [navigate]);
  const handleLocationclick = () => {
    navigate("/masters/ticketing");
  };
  const onGridReady = (params) => {
    setGridApi(params.api);
  };
  const addButtonStyles = {
    background: AddRec === "1" ? "#635bff" : "rgba(0, 0, 0, 0.12)",
    color: AddRec === "1" ? "white" : "rgba(0, 0, 0, 0.26)",
    borderRadius: "50px",
    padding: "8px 20px",
    boxShadow: AddRec === "1" ? "0px 6px 15px rgba(0, 0, 0, 0.3)" : "none",
    transition: "background 0.3s ease",
    "&:hover": {
      background: AddRec === "1" ? "#524ccc" : "rgba(0, 0, 0, 0.12)"
    },
    "&:disabled": {
      backgroundColor: "rgba(0, 0, 0, 0.12)",
      color: "rgba(0, 0, 0, 0.26)"
    }
  };

  const TruncatedCellRenderer = (props) => {
    const [isOverflowed, setIsOverflowed] = useState(false);
    const cellRef = React.useRef(null);

    useEffect(() => {
      if (cellRef.current) {
        const isTextOverflowed =
          cellRef.current.scrollWidth > cellRef.current.clientWidth ||
          (props.value && props.value.length > 20);
        setIsOverflowed(isTextOverflowed);
      }
    }, [props.value]);

    const cellValue = props.valueFormatted ? props.valueFormatted : props.value;

    return (
      <Tooltip
        title={isOverflowed ? cellValue : ''}
        placement="top"
        arrow
        disableHoverListener={!isOverflowed}
      >
        <div
          ref={cellRef}
          className="truncated-cell"
          style={{ width: '100%' }}
        >
          {cellValue}
        </div>
      </Tooltip>
    );
  };

  const StatusCellRenderer = (props) => {
    const getStatusColor = (status) => {
      switch (status) {
        case 'Active':
          return '#4caf50';
        case 'Ina ctive':
          return '#f44336';
      }
    };

    return (
      <Chip
        label={props.value}
        className="status-chip"
        sx={{
          backgroundColor: getStatusColor(props.value),
          color: 'white',
          width: '100%',
          maxWidth: '100px',
        }}
      />
    );
  };
  const columnDefs = [
    { headerName: 'Sr.No.', field: 'RowNum', width: 60, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    { headerName: 'CategoryName', field: 'TktCategoryName', minWidth: 250, flex: 1, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    { headerName: 'Property', field: 'PropName', minWidth: 250, flex: 1, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    { headerName: 'AltWardenNm', field: 'WardenNm', minWidth: 250, flex: 1, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    { headerName: 'Provider', field: 'ServiceProvName', minWidth: 250, flex: 1, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    // { headerName: 'Status', field: 'Status', width: 100, filter: 'agTextColumnFilter', cellRenderer: StatusCellRenderer },
  ];
  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    floatingFilter: true,
    filterParams: {
      suppressAndOrCondition: true,
    },
    cellStyle: {
      borderRight: '1px solid #e0e0e0',
      display: 'flex',
      alignItems: 'center'
    },
    headerClass: 'ag-header-cell',
    suppressSizeToFit: true,
  }), []);
  return (
    <Box sx={{
      width: { xs: "calc(100vw - 60px)", sm: "100%", md: "100%" },
      maxWidth: { xs: "calc(100vw - 60px)", sm: "100%", md: "100%" },
      height: '97%', display: 'flex', flexDirection: 'column', p: 2, background: 'rgb(249, 249, 249)'
    }}>
      <Box sx={{
        display: 'flex',
        justifyContent: { xs: 'center', sm: 'flex-start', md: 'space-between' },
        alignItems: 'center',
        mb: 2,
        flexWrap: 'wrap',
        rowGap: 2,
        background: 'rgb(255, 255, 255)',
        padding: '12px',
        borderRadius: '5px',
        boxShadow: 'rgb(0 0 0 / 8%)'
      }}>
        <Breadcrumbs
          aria-label="breadcrumb"
          separator="›"
          sx={{
            fontSize: { xs: "16px", sm: "16px" },
            display: "flex",
            justifyContent: { xs: 'center', sm: 'flex-start', md: 'space-between' },
            flexDirection: { xs: "column", sm: "column", md: "row" },
            alignItems: { xs: "center", sm: "flex-start" }
          }}
        >
          <Link
            underline="hover"
            color="#635bff"
            sx={{
              cursor: "pointer", fontWeight: "500", display: "flex", alignItems: "center",
              marginLeft: { xs: '30px', sm: '0px', md: '0px' },
            }}
            onClick={handleHomeClick}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            <Box component="span">Home</Box>
          </Link>

          <Link
            underline="hover"
            color="#635bff"
            sx={{ cursor: "pointer", fontWeight: "500" }}
            onClick={handleLocationclick}
          >
            Ticketing
          </Link>
          <Typography color="text.primary" sx={{ fontWeight: "500", fontSize: { xs: "18px", sm: "16px" }, }}>
            Warden/Vendor Allocation Master</Typography>
        </Breadcrumbs>

        <Box sx={{
          display: 'flex',
          alignItems: 'center',
        }}>
          <Tooltip title={AddRec !== '1' ? 'You have no permission to add record' : 'Add New Record'} placement="top" arrow>
            <span>
              <Button
                variant="contained"
                onClick={handlePermittedClick}
                size="small"
                disabled={AddRec !== '1'}
                sx={addButtonStyles}
              >
                <AddIcon /> New
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Paper
        elevation={2}
        sx={{
          // height: 'calc(100vh - 250px)',
          height: 'auto', minHeight: 'calc(100vh - 250px)',
          width: '100%',
          flexGrow: 1,
          overflow: 'hidden'
        }}
      >
        <Box
          className="ag-theme-alpine"
          style={{
            height: '100%',
            width: '100%',
            overflowX: 'auto'
          }}
        >
          {isLoading ? (
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
              textAlign: 'center',
            }}
            >
              <CircularProgress size='3rem' sx={{ color: '#635bff' }} />
              <Typography sx={{ marginTop: 2, color: '#635bff', fontWeight: 'bold' }}>
                Loading Data...
              </Typography>
            </Box>
          ) : (
            <AgGridReact
              columnDefs={columnDefs}
              rowData={rows}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              onRowDoubleClicked={handleRowDoubleClick}
              pagination={true}
              paginationPageSize={1000}
              paginationPageSizeSelector={[50, 100, 500, 1000, 5000]}
              suppressRowClickSelection={true}
              rowSelection={'single'}
              enableCellTextSelection={true}
              ensureDomOrder={true}
              loading={isLoading}
              rowHeight={28}
              headerHeight={28}
              floatingFiltersHeight={28}
              overlayLoadingTemplate={
                '<span class="ag-overlay-loading-center">Loading data...</span>'
              }
              overlayNoRowsTemplate={
                '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow;">No data found</span>'
              }
              suppressMenuHide={true}
              enableRangeSelection={true}
              sideBar={false}
              suppressHorizontalScroll={false}
            />
          )}
        </Box>

      </Paper>
      <Typography variant="body2" sx={{
        fontWeight: 'bold', position: 'absolute', bottom: '55px',
        display: { xs: 'none', sm: 'block', md: 'block' },
        marginLeft: '10px'
      }}>
        Total Records: {rows.length}
      </Typography>


    </Box>
  );
}
